<template>
    <v-form autocomplete="off">
        <v-card>
            <v-toolbar dark color="primary">
                <v-toolbar-title>{{ formTitle }}</v-toolbar-title>
                <slot name="header"></slot>
            </v-toolbar>
            <v-card-text>
                <v-container grid-list-md>
                    <v-layout wrap>
                        <v-flex xs12>
                            <v-combobox
                                name="description"
                                label="What is the task?"
                                :items="requirements"
                                item-value="description"
                                item-text="description"
                                v-model="value.description"
                                v-validate="'required'"
                                :error-messages="errors.collect('description')"
                                :return-object="false"
                                :disabled="editing"
                                autocomplete="off"
                            ></v-combobox>
                        </v-flex>
                        <v-flex xs12>
                            <v-menu
                                v-model="initialDateDialog"
                                max-width="290px"
                                :close-on-content-click="false"
                            >
                                <template v-slot:activator="{ on }">
                                    <v-text-field
                                        :value="formattedInitialDate"
                                        label="What is the date?"
                                        readonly
                                        v-validate="'required'"
                                        :error-messages="errors.collect('date')"
                                        name="date"
                                        v-on="on"
                                        required
                                        append-icon="event"
                                        @click:append="initialDateDialog = true"
                                    ></v-text-field>
                                </template>
                                <v-date-picker
                                    v-model="value.initialDate"
                                    @change="initialDateDialog = false"
                                    no-title
                                ></v-date-picker>
                            </v-menu>
                        </v-flex>
                        <v-flex xs12>
                            <v-checkbox
                                label="This is a recurring task"
                                v-model="value.recurring"
                                name="recurring"
                                :disabled="disabled"
                            ></v-checkbox>
                        </v-flex>
                        <v-flex xs5 v-if="!!value.recurring">
                            <v-text-field
                                label="How often does this occur?"
                                placeholder="e.g. 6"
                                name="frequency"
                                v-model="value.interval"
                                v-validate="!!value.recurring ? 'required|freqIsInteger' : 'freqIsInteger'"
                                :error-messages="errors.collect('frequency')"
                                type="number"
                                prefix="Every:"
                                class="centered-input"
                                :disabled="disabled"
                            ></v-text-field>
                        </v-flex>
                         <v-flex xs4 v-if="!!value.recurring">
                            <v-select
                                label="months / days"
                                item-value="key"
                                item-text="value"
                                :items="intervalTypes"
                                v-model="value.intervalType"
                                v-validate="'required'"
                                :error-messages="errors.collect('interval type')"
                                name="interval type"
                                required
                            ></v-select>
                        </v-flex>
                        <v-flex xs12 v-if="!!value.recurring">
                            <div>When does this repeat?</div>
                            <v-radio-group
                                name="recurrence"
                                v-model="value.recurrence"
                                v-validate="!!value.recurring ? 'required' : null"
                                :error-messages="errors.collect('recurrence')"
                                :disabled="disabled"
                            >
                                <v-radio value="OnDueDate">
                                    <template
                                        v-slot:label
                                    >{{ value.interval }} {{ value.intervalType }} after it was last due
                                    </template>
                                </v-radio>
                                <v-radio value="OnCompletionDate">
                                    <template
                                        v-slot:label
                                    >{{ value.interval }} {{ value.intervalType }} after it was last completed
                                    </template>
                                </v-radio>
                            </v-radio-group>
                        </v-flex>
                        <v-flex xs5>
                            <v-checkbox
                                label="Warning required"
                                v-model="value.warningRequired"
                                name="warning"
                            ></v-checkbox>
                        </v-flex>
                        <v-flex xs3 v-if="!!value.warningRequired">
                            <v-text-field
                                placeholder="e.g. 6"
                                name="warning frequency"
                                v-model="value.warningInterval"
                                v-validate="!!value.warningRequired ? 'required|freqIsInteger' : 'freqIsInteger'"
                                :error-messages="errors.collect('warning frequency')"
                                type="number"
                                class="centered-input"
                                :disabled="disabled"
                            ></v-text-field>
                        </v-flex>
                        <v-flex xs4 v-if="!!value.warningRequired">
                            <v-select
                                label="months / days"
                                item-value="key"
                                item-text="value"
                                :items="intervalTypes"
                                v-model="value.warningIntervalType"
                                v-validate="'required'"
                                :error-messages="errors.collect('warning interval type')"
                                name="warning interval type"
                                required
                            ></v-select>
                        </v-flex>
                    </v-layout>
                </v-container>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn @click="close">Cancel</v-btn>
                <v-btn color="primary" :disabled="disableSubmit" @click="submit">{{ submitText }}</v-btn>
            </v-card-actions>
        </v-card>
    </v-form>
</template>

<script>
  import {ConfigurationType, IntervalType} from '../../models/enums';
  import ServiceRequirements from '../ServiceRequirements/ServiceRequirementsClient.vue';
  import DateBasedTasksClient from '../DateBasedTasks/DateBasedTasksClient.vue';
  import moment from 'moment';
  import {DateFormat} from '../../plugins/date-filter';
  import FormValidation from '../FormValidation.vue';
  import {Validator} from 'vee-validate';

  export default {
    props: {
      value: {
        required: true
      }
    },
    mixins: [FormValidation, ServiceRequirements, DateBasedTasksClient],
    async mounted() {
      this.requirements = await this.getDateBasedTaskDescriptionLookups();
    },
    computed: {
      formTitle() {
        return !this.editing ? 'Add Date-Based task' : 'Update Date-Based Task';
      },
      submitText() {
        return !this.editing ? 'Create new Date-Based task' : 'Save changes';
      },
      editing() {
        return !!this.value.id;
      },
      disabled() {
        return !!this.value.id && !this.userIsFleetManager;
      },
      formattedInitialDate() {
        return this.value.initialDate ? moment(this.value.initialDate).format(DateFormat.DefaultDisplay) : '';
      }
    },
    methods: {
      submitForm() {
        if (!this.value.recurring) {
          this.value.interval = null;
          this.value.recurrence = 'none';
        }
        this.addOrUpdate();
      },
      addOrUpdate() {
        if (!this.editing) {
          this.$emit('add', {id: this.id, model: this.value});
        } else {
          this.$emit('edit', {id: this.id, model: this.value});
        }
      },
      close() {
        this.$emit('close');
      }
    },
    data() {
      return {
        id: '',
        configurationTypes: Object.keys(ConfigurationType)
          .map(key => ConfigurationType[key])
          .sort(),
        description: '',
        requirement: {},
        today: moment().format(DateFormat.Canonical),
        initialDateDialog: false,
        rules: {
          required: value => !!value || 'Required'
        },
        requirements: [],
        intervalTypes: [
          {
            key: IntervalType.Months,
            value: 'months'
          },
          {
            key: IntervalType.Days,
            value: 'days'
          }
        ]
      };
    },
    watch: {
      value: {
        handler(val) {
          if (val.id) {
            this.id = val.id;
          } else {
            this.id = this.$uuid.v1();
          }
        },
        immediate: true
      }
    },
    async created() {
      const freqIsInteger = (value) => {
        const pattern = /^([1-9][0-9]?[0-9]?)$/;
        return {
          valid : pattern.test(value),
          data: {
            message: 'The frequency field must be between 1 and 999'
          }
        };
      };

      Validator.extend('freqIsInteger', {
        validate: freqIsInteger,
        getMessage: (field, params, data) => data.message
      });
    }
  };
</script>

<style>
    .centered-input input {
        text-align: center;
    }
</style>
